import { cn } from '@shadcn/lib/utils';
import { Menu } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Logo } from '../../atoms/logo/logo';
import { DropdownMenu, MenuItem } from '../../molecules/dropdown-menu/dropdown-menu';

type AppBarProps = {
  readonly className?: string;
  readonly menuItems?: readonly MenuItem[];
  readonly onNavigate?: (href: string) => void;
  readonly 'aria-label'?: string;
  readonly 'data-testid'?: string;
  readonly logoHref?: string;
  readonly showMenu?: boolean;
};

function AppBar({
  className,
  menuItems,
  onNavigate,
  'aria-label': ariaLabel,
  'data-testid': testId,
  logoHref,
  showMenu = true,
}: AppBarProps) {
  const { t } = useTranslation();

  const logo = <Logo size="md" />;

  return (
    <header
      aria-label={ariaLabel ?? t('navigation.main')}
      className={cn('sticky top-0 z-10 py-1.5 sm:py-1.5 bg-background shadow-md', className)}
      data-testid={testId}
      role="banner"
    >
      <div className="container flex items-center justify-between">
        {logoHref ? (
          <Link aria-label={t('navigation.home')} to={logoHref}>
            {logo}
          </Link>
        ) : (
          logo
        )}
        {showMenu && (
          <DropdownMenu
            aria-label={t('navigation.userMenu')}
            data-testid={testId ? `${testId}-menu` : undefined}
            items={menuItems}
            onNavigate={onNavigate}
          >
            <button
              aria-label={t('navigation.openUserMenu')}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 w-10"
              type="button"
            >
              <Menu className="h-6 w-6" />
              <span className="sr-only">{t('navigation.userMenu')}</span>
            </button>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}

AppBar.displayName = 'AppBar';

export { AppBar };
